import { z } from "zod";
import { CheckResult, CheckSeverity, IsoDateTime } from "./common.ts";
import { ReasonCategory } from "./reasons.ts";

// Assessment DTOs v1 (change split-core-into-trust-contexts, design D2/D6; ADR 0018): what Location
// Intelligence, Device Intelligence and the Risk Engine return to the Decision Engine for one
// Interaction through their facades (`LocationFacade.assess`, `DeviceFacade.assess`,
// `RiskFacade.assess`), and the subdocuments of `DecisionMadeV1`. Serializable and strict (ADR 0001
// R5). Field names follow today's `core` code (location sources, KnownLocation, check results) and
// the `VerificationCompleted` v1 payload, so the moves of the later slices map 1:1. Never precise
// coordinates, raw signals, tokens or PII.

const Iso3166Country = z.string().regex(/^[A-Z]{2}$/);
const Iso3166Subdivision = z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/);
/** Precision-5 geohash cell (≈ 4.9 km × 4.9 km); never a precise coordinate. */
const Geohash5 = z.string().regex(/^[0-9b-hjkmnp-z]{5}$/);

/** The location sources of `core` (`LocationSource`): `ip`, `browser`, `wifi`. */
export const LOCATION_SOURCE_KINDS = ["ip", "browser", "wifi"] as const;
export const LocationSourceKind = z.enum(LOCATION_SOURCE_KINDS);
export type LocationSourceKind = z.infer<typeof LocationSourceKind>;

/** The primary source (`PrimaryLocationSelector`): the browser when available, else the IP, else none. */
export const PrimarySource = z.enum(["browser", "ip", "none"]);
export type PrimarySource = z.infer<typeof PrimarySource>;

/**
 * One evaluated source of an Interaction's location, coarse only: the same shape as a source of
 * `VerificationCompletedV1.location.sources` (parity asserted by a contract test). v0: `ip` carries
 * the country only, `browser` geohash5, accuracy and the resolved area, `wifi` is always unavailable.
 */
export const AssessedLocationSourceV1 = z
  .object({
    kind: LocationSourceKind,
    availability: z.enum(["available", "unavailable"]),
    unavailable_cause: z.string().optional(),
    country: Iso3166Country.optional(),
    subdivision: Iso3166Subdivision.optional(),
    municipality: z.string().optional(),
    municipality_name: z.string().min(1).max(120).optional(),
    postal_code: z.string().optional(),
    geohash5: Geohash5.optional(),
    accuracy_km: z.number().nonnegative().optional(),
    border_uncertain: z.boolean().optional(),
  })
  .strict();
export type AssessedLocationSourceV1 = z.infer<typeof AssessedLocationSourceV1>;

/**
 * Country (ISO 3166-1), optional state (ISO 3166-2) and municipality resolved for the primary
 * source (`AdministrativeArea` of `core`); an IP-primary location has the country only.
 */
export const AdministrativeAreaV1 = z
  .object({
    country: Iso3166Country,
    subdivision: Iso3166Subdivision.optional(),
    municipality: z
      .string()
      .regex(/^[A-Za-z0-9-]{1,16}$/)
      .optional(),
    municipality_name: z.string().min(1).max(120).optional(),
    border_uncertain: z.boolean(),
  })
  .strict();
export type AdministrativeAreaV1 = z.infer<typeof AdministrativeAreaV1>;

/**
 * The primary source's position at the Interaction time (`KnownLocation` of `core`, design D17 of
 * add-explorer-mvp): geohash5 cell, accuracy radius (km) and time. Input of the `travel_speed`
 * check; never precise coordinates.
 */
export const KnownLocationV1 = z
  .object({
    geohash5: Geohash5,
    accuracy_km: z.number().nonnegative(),
    at: IsoDateTime,
  })
  .strict();
export type KnownLocationV1 = z.infer<typeof KnownLocationV1>;

/** A field a browser cannot provide (`mac`, `ssid`, `bssid`) and why. */
export const AbsentFieldV1 = z.object({ field: z.string(), cause: z.string() }).strict();

/**
 * Location Intelligence → Decision Engine (`LocationFacade.assess`), and `assessments.location` of
 * `DecisionMadeV1`. The precise coordinates stay sealed in Location (`sealPrecise` /
 * `readPrecise`); this DTO never carries them (strict: `latitude`, `longitude` or `coordinates`
 * are rejected).
 */
export const LocationAssessmentV1 = z
  .object({
    primary_source: PrimarySource,
    /** Every evaluated source (may be empty when nothing was evaluated). */
    sources: z.array(AssessedLocationSourceV1),
    /** Area of the primary source; absent when it is `none` or the point is in no area (at sea). */
    area: AdministrativeAreaV1.optional(),
    /** Absent when the primary source has no position (IP-only, country precision, or none). */
    known_location: KnownLocationV1.optional(),
    /** Country of the IP source from the IpRegistryDataset (ADR 0004); absent when unavailable. */
    ip_country: Iso3166Country.optional(),
    ip_dataset_version: z.string().min(1).optional(),
    boundary_dataset_version: z.string().min(1).optional(),
    absent_fields: z.array(AbsentFieldV1).optional(),
  })
  .strict()
  .superRefine((assessment, ctx) => {
    const primary = assessment.primary_source;
    if (
      primary !== "none" &&
      !assessment.sources.some((s) => s.kind === primary && s.availability === "available")
    )
      ctx.addIssue({
        code: "custom",
        path: ["primary_source"],
        message: "the primary source must be an available source of that kind",
      });
    if (
      primary === "none" &&
      (assessment.area !== undefined || assessment.known_location !== undefined)
    )
      ctx.addIssue({
        code: "custom",
        path: ["primary_source"],
        message: "an area or known location needs a primary source",
      });
  })
  .meta({ id: "LocationAssessmentV1" });
export type LocationAssessmentV1 = z.infer<typeof LocationAssessmentV1>;

/** Snake_case reason code of the reason catalog (`ReasonCode` of `core`). */
const ReasonCodeField = z.string().regex(/^[a-z][a-z0-9_]{2,63}$/);

/** One reason with its catalog category, as in `VerificationCompletedV1.reasons`. */
export const ReasonRefV1 = z.object({ code: ReasonCodeField, category: ReasonCategory }).strict();
export type ReasonRefV1 = z.infer<typeof ReasonRefV1>;

/**
 * The result of one evaluated check, as in `VerificationCompletedV1.checks` (`VerificationCheck`
 * of `core`): `reason` is the catalog reason it contributes, `cause` the free-form cause shown in
 * `checks[].cause`.
 */
function checkOutcome<const C extends string>(code: C) {
  return z
    .object({
      code: z.literal(code),
      severity: CheckSeverity,
      required: z.boolean(),
      result: CheckResult,
      reason: ReasonCodeField.optional(),
      cause: z.string().optional(),
    })
    .strict()
    .refine((check) => check.result !== "pass" || check.reason === undefined, {
      message: "a passed check carries no reason",
      path: ["reason"],
    });
}

export const IntegrityCheckOutcomeV1 = checkOutcome("integrity");
export const AutomationCheckOutcomeV1 = checkOutcome("automation");
export const TravelSpeedCheckOutcomeV1 = checkOutcome("travel_speed");

/**
 * Device Intelligence → Decision Engine (`DeviceFacade.assess`), and `assessments.device` of
 * `DecisionMadeV1`: the `device_ref` handling of today (`DeviceRef.parseOptional`: an absent or
 * malformed value yields none and the advisory reason `device_ref_missing`; `device_ref_reset` as
 * the SDK reported it) and the `integrity` (SDK version) and `automation` (`navigator.webdriver`)
 * check results. No raw device signal.
 */
export const DeviceAssessmentV1 = z
  .object({
    /** The validated `device_ref` (UUID v4, lower case); absent when missing or malformed. */
    device_ref: z.uuidv4().optional(),
    device_ref_reset: z.boolean(),
    /** True exactly when no valid `device_ref` was sent (reason `device_ref_missing`). */
    device_ref_missing: z.boolean(),
    /** The automation indicator the SDK reported (`navigator.webdriver`). */
    webdriver: z.boolean(),
    integrity: IntegrityCheckOutcomeV1,
    automation: AutomationCheckOutcomeV1,
  })
  .strict()
  .refine((device) => device.device_ref_missing === (device.device_ref === undefined), {
    message: "device_ref_missing must be true exactly when device_ref is absent",
    path: ["device_ref_missing"],
  })
  .meta({ id: "DeviceAssessmentV1" });
export type DeviceAssessmentV1 = z.infer<typeof DeviceAssessmentV1>;

/**
 * Risk Engine → Decision Engine (`RiskFacade.assess(findings, history)`), and `assessments.risk`
 * of `DecisionMadeV1`: the `travel_speed` result of `ImpossibleTravelPolicy` (against the
 * player's last allowed known location and the Tenant Policy's `max_travel_speed_kmh`) and the
 * reasons the findings map to through the reason catalog (v1: no score).
 */
export const RiskAssessmentV1 = z
  .object({
    travel_speed: TravelSpeedCheckOutcomeV1,
    /** Reasons of the findings, in catalog order, each with its category. */
    reasons: z.array(ReasonRefV1),
  })
  .strict()
  .meta({ id: "RiskAssessmentV1" });
export type RiskAssessmentV1 = z.infer<typeof RiskAssessmentV1>;

/**
 * Decision Engine → Risk Engine (`RiskFacade.assess(findings, history)`, change
 * split-core-into-trust-contexts task 4.1): the findings the Risk Engine evaluates for one
 * Interaction. v1: the primary source's known location (absent when the location is IP-only or
 * unknown) and the Tenant Policy's `max_travel_speed_kmh` (absent → the default, 900 km/h). The
 * bounds of the speed (100–2,000) are the Risk Engine's rule (`MaxTravelSpeed`), not a transport
 * rule, so an out-of-bounds value reaches it and is refused there (`invalid_max_travel_speed`).
 */
export const RiskFindingsV1 = z
  .object({
    known_location: KnownLocationV1.optional(),
    max_travel_speed_kmh: z.number().optional(),
  })
  .strict()
  .meta({ id: "RiskFindingsV1" });
export type RiskFindingsV1 = z.infer<typeof RiskFindingsV1>;

/**
 * The player's travel history as the Decision Engine read it from its own store (the data stays
 * where it lives; ADR 0018): `read` with the most recent previous **allowed** known location in the
 * same Tenant (absent → none yet), or `unavailable` when it could not be read. With a known current
 * location an `unavailable` history fails closed (`travel_history_unavailable` → `503`).
 */
export const TravelHistoryV1 = z
  .discriminatedUnion("status", [
    z.object({ status: z.literal("read"), previous_allowed: KnownLocationV1.optional() }).strict(),
    z.object({ status: z.literal("unavailable") }).strict(),
  ])
  .meta({ id: "TravelHistoryV1" });
export type TravelHistoryV1 = z.infer<typeof TravelHistoryV1>;
